import { doc, getDoc, onSnapshot, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '~/config/firebase.config';
import { storedSignupFlow } from '~/utils/signupFlow';
import { FirestoreConverter } from '../../converter';
import { UserConverter, ClientUserConverter } from './user.converter';

/**
 * `signupLang` (« en-GB ») et `signupTz` (« Europe/London ») du navigateur, et `signupFlow`
 * (version du test A/B d'inscription, utils/signupFlow), champs absents si inconnus.
 */
function browserAtSignup(): { signupLang?: string; signupTz?: string; signupFlow?: string } {
    const out: { signupLang?: string; signupTz?: string; signupFlow?: string } = {};
    const flow = storedSignupFlow();
    if (flow) out.signupFlow = flow;
    try {
        const lang = typeof navigator !== 'undefined' ? navigator.language : '';
        if (lang) out.signupLang = lang.slice(0, 35);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) out.signupTz = tz.slice(0, 64);
    } catch {
        /* navigateur sans Intl : la relance retombe sur le domaine de l'email */
    }
    return out;
}

export class UserDatabase {
    /**
     * Subscribe to user changes
     * @param userID the user identifier
     * @returns a subscriber
     */
    static subscribe(userID: string) {
        if (!userID || userID === 'unknown') {
            return (_onValuehange: () => void) => () => {};
        }
        const ref = doc(db, 'users', userID);
        return (onValuehange: () => void) => onSnapshot(ref, onValuehange);
    }

    /**
     * Get a user from the firestore database
     * @param userID the user identifier
     * @returns the corresponding user classe
     */
    static async getClientUser(userID: string) {
        if (!userID || userID === 'unknown') {
            throw new Error(`Invalid user id`);
        }
        return UserDatabase._getClientUserByID(userID, ClientUserConverter);
    }

    /**
     * Get a user from the firestore database
     * @param userID the user identifier
     * @returns the corresponding user classe
     */
    static async getUser(userID: string) {
        if (!userID || userID === 'unknown') {
            throw new Error(`Invalid user id`);
        }
        return UserDatabase._getClientUserByID(userID, UserConverter);
    }

    /**
     * Create an user to the firestore database
     */
    static async createUser(uid: string, email: string, avatar: string, firstname: string, lastname: string) {
        const created = await UserDatabase.ensureUser(uid, email, avatar, firstname, lastname);
        console.log(created ? `User '${uid}' created in database.` : `User '${uid}' already exists.`);
    }

    /**
     * Garantit l'existence de `users/{uid}` (idempotent) : crée le document
     * s'il manque, ne touche à rien s'il existe déjà.
     *
     * Transaction plutôt que getDoc + setDoc : plusieurs appelants peuvent
     * courir en même temps (page signin/signup, auto-réparation de Home,
     * webhook Stripe qui écrit `sites.*`) — sans transaction, un setDoc tardif
     * écraserait un document déjà enrichi. Voir gotcha #170.
     *
     * @returns `true` si le document a été créé, `false` s'il existait.
     */
    static async ensureUser(uid: string, email: string, avatar: string, firstname = '', lastname = '') {
        const docRef = doc(db, 'users', uid);
        return runTransaction(db, async (transaction) => {
            const snap = await transaction.get(docRef);
            if (snap.exists()) return false;
            const serverTime = serverTimestamp();
            transaction.set(docRef, {
                id: uid,
                email,
                avatar,
                firstname,
                lastname,
                firstAccess: serverTime,
                lastAccess: serverTime,
                sites: {},
                // Langue et fuseau du navigateur à la création : la relance des inscriptions inachevées
                // (b2b signupJob) s'en sert quand le compte s'est arrêté avant la page de paiement.
                ...browserAtSignup(),
            });
            return true;
        });
    }

    private static async _getClientUserByID<Type>(userID: string, converter: FirestoreConverter<Type>): Promise<Type> {
        const userRef = doc(db, 'users', userID).withConverter(converter);
        const user = await getDoc(userRef)
            .then((snap) => {
                if (snap.exists()) {
                    return snap.data();
                }
                throw new Error(`User '${userID}' does not exist.`);
            })
            .catch((err) => {
                throw new Error(`Cannot get user document with ref '/users/${userID}\n[code=${err.code}]'`);
            });
        return user;
    }
}
